import React from 'react';

/**
 * Hero component that displays a hero section with optional subTitle, title, description, call to action, and banner.
 *
 * @component
 * @param {Object} props - The props object.
 * @param {string|Object} [props.subTitle] - The subTitle text or a custom element to display. If a string is provided, it will be displayed as a text span. If an object with a `custom` property is provided, the custom element will be rendered.
 * @param {string|Object} [props.title] - The title text or a custom element to display. If a string is provided, it will be displayed as an `h1` element. If an object with a `custom` property is provided, the custom element will be rendered.
 * @param {string|Object} [props.description] - The description text or a custom element to display. If a string is provided, it will be displayed as a `p` element. If an object with a `custom` property is provided, the custom element will be rendered.
 * @param {React.ReactNode} [props.callToAction] - The call to action element to display. This can be any valid React node.
 * @param {Object} [props.banner] - The banner object containing the image source, alt text, and optionally a custom element.
 * @param {string} [props.banner.image] - The source URL of the banner image.
 * @param {string} [props.banner.alt] - The alt text for the banner image.
 * @param {React.ReactNode} [props.banner.custom] - A custom banner element to display instead of the image.
 * @returns {JSX.Element} The rendered Hero component.
 */
const Hero = ({ subTitle, title, description, callToAction, banner }) => {

    return (
        <div className="sec-hero">
            <div className="sec-hero-text">
                <div className="hero-text dark-hero" data-aos="fade-up" data-aos-delay="150">
                    {subTitle && <span>{subTitle}</span>}
                    {subTitle?.custom && subTitle.custom}

                    {title && <h1>{title}</h1>}
                    {title?.custom && title.custom}

                    {description && <p>{description}</p>}
                    {description?.custom && description.custom}

                    {callToAction && callToAction}
                </div>
            </div>
            <div className="sec-banner vivify fadeIn duration-300 delay-200">
                {banner?.image && banner?.alt && <img src={banner.image} alt={banner.alt} />}
                {banner?.video && banner.video && <video
                    poster={banner.thumbnail}
                    autoPlay muted loop playsInline src={banner.video} />}
                {banner?.custom && banner.custom}
            </div>
        </div>
    );
}

export default Hero;
